import {
  ACCOUNTS_COLUMNS,
  CASH_DRAWERS_ACTIVITY_COLUMNS,
  ADJUSTMENT_COLUMNS,
  FINISHED_GOODS_COLUMNS,
  SUPPLIER_TYPE_SETTINGS_COLUMNS,
  VENDOR_CLASS_SETTINGS_COLUMNS,
  PAYMENT_TERMS_COLUMNS,
  DRIVER_COLUMNS,
  ASSET_TYPES_SETTINGS_COLUMNS,
  FEE_OR_PROMOTION_COLUMNS,
} from 'components/containers/settings/utils/columnsAndRows'
import {
  ACTIVITY_TYPE_OPTIONS,
  AUTO_CLOSE_CONTRACT_OPTIONS,
  PRECISION_OPTIONS,
  PAYMENT_ROUNDING_OPTIONS,
  ACCOUNT_PAYMENT_TYPES,
  THRESHOLD_UM_OPTIONS,
  NUMBER_OF_AXLES_OPTIONS,
  PAYMENT_ROUNDING_DIRECTION_OPTIONS,
  DOCUMENT_TYPE_ARRAY,
  INBOUND_TICKET_FIELD_ARRAY,
  DISPATCH_TICKET_TYPES_ARRAY,
} from 'utils/constants'
import { COMPLIANCE_FIELDS_ARRAY } from 'components/containers/bills/bills-constants'
import { BILL_SYNC_DATE_SOURCE_OPT } from 'components/containers/ledger/constants.ts'

export const SETTINGS_LAYOUTS = {
  overview: {
    category: 'overviewSettings',
    subCategory: 'overview',
    settingsList: [
      {
        type: 'currencyConversion',
        treatment: 'GREEN-5540-currency-exchange-rates',
        title: 'Currency Conversion Rates',
        subTitle: 'If you want to change your default currency, please reach out to your GreenSpark representative.',
      },
      {
        type: 'lockAccounting',
      },
    ],
  },
  documents: {
    category: 'documentSettings',
    subCategory: 'general',
    settingsList: [
      {
        type: 'list',
        title: 'Email Preferences',
        subTitle:
          "Set up the email configurations for customer communications. You can customize the sender's name for outgoing emails, add a CC email address to monitor all correspondence, and designate a reply-to email address for customer responses.",
        treatment: 'enable-pdf-emails',
        listComponents: [
          {
            componentType: 'input',
            labelInput: 'Name on Outgoing Emails',
            path: 'documentSettings.general.emailPreferences.fromName',
          },
          {
            componentType: 'input',
            labelInput: 'CC Email Address',
            path: 'documentSettings.general.emailPreferences.cc',
          },
          {
            componentType: 'input',
            labelInput: 'Reply-To Email Address',
            path: 'documentSettings.general.emailPreferences.replyTo',
          },
        ],
      },
      {
        type: 'list',
        title: 'Operator Names on Receipts',
        subTitle: "When checked, the operator's last name will appear on inbound ticket receipts.",
        listComponents: [
          {
            componentType: 'checkbox',
            label: "Include operator's last name on receipts",
            path: 'documentSettings.general.includeOperatorLastName',
          },
        ],
      },
      {
        type: 'list',
        title: 'Include Yard Address in Printed Document Headers',
        subTitle: `When checked, the yard address will appear in the header of all printed documents.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Include yard address in printed document headers',
            path: 'documentSettings.general.includeAddressInDocumentHeader',
          },
        ],
      },
      {
        type: 'doubleInput',
        title: 'Inbound Ticket Disclaimers',
        subTitle: 'Edit the disclaimers that display on your printed Inbound Tickets',
        label1: 'Scale Ticket Disclaimer',
        path1: 'documentSettings.general.scaleTicketDisclaimer',
        label2: 'Purchase Ticket Disclaimer',
        path2: 'documentSettings.general.purchaseTicketDisclaimer',
      },
      {
        treatment: 'GREEN-9774-printed-doc-compliance',
        type: 'input',
        title: 'Weighmaster Certificate',
        subTitle: 'Edit the text that appears on the weighmaster certificate portion of your printed inbound tickets',
        path: 'documentSettings.general.weighmasterCertificate',
      },
      {
        type: 'list',
        title: 'Outbound Ticket Disclaimers',
        subTitle: 'Edit the text that appears on display on your printed Outbound Tickets',
        treatment: 'GREEN-11284-fe-pdfs',
        listComponents: [
          {
            componentType: 'input',
            labelInput: 'Packing List Disclaimer',
            path: 'documentSettings.general.packingListDisclaimer',
            multiline: true,
          },
          {
            componentType: 'input',
            labelInput: 'Bill of Lading Disclaimer',
            path: 'documentSettings.general.billOfLadingDisclaimer',
            multiline: true,
          },
          {
            componentType: 'input',
            labelInput: 'Invoice Disclaimer',
            path: 'documentSettings.general.invoiceDisclaimer',
            multiline: true,
          },
        ],
      },
      {
        type: 'list',
        title: 'Show Time Stamps on all Printed/Emailed Docs',
        subTitle: 'For the documents chosen, time stamps will be included in dates on the documents.',
        treatment: 'GREEN-11284-fe-pdfs',
        listComponents: [
          {
            componentType: 'multiSelect',
            items: DOCUMENT_TYPE_ARRAY,
            path: 'documentSettings.general.documentsWithTimestamps',
            labelInput: 'Label input',
          },
        ],
      },
      {
        type: 'list',
        title: 'Show QR Codes on Printed Documents',
        subTitle:
          'For the documents chosen, QR codes will be displayed on the documents. These QR codes can be scanned to find the related ticket in GreenSpark.',
        treatment: 'GREEN-11284-fe-pdfs',
        listComponents: [
          {
            componentType: 'multiSelect',
            items: DOCUMENT_TYPE_ARRAY.filter(doc => doc.id === 'IBT_SCALE_TICKET' || doc.id === 'IBT_PURCHASE_TICKET'),
            path: 'documentSettings.general.documentsWithQRCodes',
            labelInput: 'Selected Documents',
          },
        ],
      },
      {
        type: 'list',
        title: 'Put Organization Logo on Separate Row',
        subTitle: `When checked, the organization logo will appear on its onw row in the header of all printed documents.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Put Organization Logo on Separate Row',
            path: 'documentSettings.general.logoOnSeparateRow',
          },
        ],
      },
    ],
  },
  adjustments: {
    category: 'purchaseSettings',
    subCategory: 'deductions',
    settingsList: [
      {
        type: 'grid',
        title: 'Fees And Promotions',
        subTitle:
          'Add and edit fees and promotions that apply to inbound tickets. ex. a service fee on all check payments',
        rowType: 'Fee Or Promotion',
        treatment: 'GREEN-12331-fees-and-promotions',
        generateColumns: FEE_OR_PROMOTION_COLUMNS,
        path: 'purchaseSettings.feesAndPromotions',
        dataGridProps: {
          density: 'standard',
        },
      },
      {
        type: 'grid',
        title: 'Adjustments',
        subTitle: `Below is a list of adjustments you can apply to materials on both Inbound Tickets and Invoices using Consumer Weights.
        You can add custom adjustments to the list, as well as turn them into presets by adding at least one of the following: Type, Amount, or UM.`,
        rowType: 'Adjustment',
        generateColumns: ADJUSTMENT_COLUMNS,
        path: 'purchaseSettings.deductions.listOfDeductions',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  'purchase-orders': {
    category: 'contractSettings',
    subCategory: 'purchaseOrders',
    settingsList: [
      {
        type: 'input',
        title: 'Disclaimer',
        subTitle: 'Edit the disclaimer that displays on your purchase orders',
        label: 'Purchase Order Disclaimer',
        path: 'contractSettings.purchaseOrders.purchaseOrderDisclaimer',
      },
    ],
  },
  'sales-orders': {
    category: 'contractSettings',
    subCategory: 'salesOrders',
    settingsList: [
      {
        type: 'input',
        title: 'Disclaimer',
        subTitle: 'Edit the disclaimer that displays on your sales orders',
        label: 'Sales Order Disclaimer',
        path: 'contractSettings.salesOrders.salesOrderDisclaimer',
      },
    ],
  },
  'payment-terms': {
    category: 'contractSettings',
    subCategory: 'paymentTerms',
    settingsList: [
      {
        type: 'grid',
        title: 'Payment Terms',
        subTitle: 'Manage payment terms that are available to select on your Purchase and Sales orders.',
        rowType: 'Payment Term',
        generateColumns: PAYMENT_TERMS_COLUMNS,
        path: 'contractSettings.paymentTerms.listOfPaymentTerms',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  'inbound-tickets': {
    category: 'purchaseSettings',
    subCategory: 'inboundTickets',
    settingsList: [
      {
        type: 'list',
        treatment: 'GREEN-12153-Customize-The-IBT-Card-View',
        title: 'Customize Inbound Ticket Fields',
        subTitle: `Choose which fields display on the Card View for Inbound Tickets`,
        itemsInRowDirection: false,
        listComponents: [
          {
            componentType: 'multiSelect',
            items: INBOUND_TICKET_FIELD_ARRAY,
            path: 'purchaseSettings.inboundTickets.fieldVisibility',
            labelInput: 'Fields',
          },
        ],
      },
      {
        type: 'list',
        title: 'Add Customers on Inbound Tickets',
        subTitle: `This field will allow users to add new customers directly from an inbound
        ticket instead of needing to add the customer in the Customers module first.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Allow users to add customers from an inbound ticket',
            path: 'purchaseSettings.inboundTickets.addCustomersOnInboundTickets',
          },
        ],
      },
      {
        type: 'list',
        title: 'Automatically Populate Default Retail Customer',
        subTitle:
          'If a user does not add a customer to an inbound ticket, a "Default Retail" customer will be added to the ticket upon creation.',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Automatically populate a "Default Retail" customer to Inbound Tickets if one is not selected',
            path: 'purchaseSettings.inboundTickets.useDefaultRetailCustomer',
          },
        ],
      },
      // {
      //   type: 'columnEdit',
      //   title: 'Inbound Ticket Material Fields',
      //   subTitle: `This is a preview of the materials table on your Inbound Tickets.
      //   Add, remove, and reorder the columns as you see fit.`,
      //   generateColumns: SAMPLE_EDIT_COLUMNS,
      //   rows: SAMPLE_EDIT_ROWS,
      //   dataGridProps: {},
      // },
      {
        type: 'list',
        treatment: 'GREEN-5551-Flag-Manual-Scale-Read-Override',
        title: 'Flag Manually Entered Weights on Inbound Tickets',
        subTitle: `When someone types a number into a weight box on an inbound ticket, we'll mark it with a star (*).
          This lets you easily spot weights that might have been tweaked from the scale's original reading.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Flag manually entered weights on inbound tickets',
            path: 'purchaseSettings.inboundTickets.flagManuallyEnteredWeights',
          },
        ],
      },
      {
        type: 'list',
        title: 'Automatically Save & Close Inbound Ticket after Printing',
        subTitle: `When activated, GreenSpark will automatically save and close your inbound ticket once
          the documents checked below are printed, streamlining your scale house operations and
          preventing accidental mix-ups.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Automatically save & close inbound ticket after printing receipt',
            path: 'purchaseSettings.inboundTickets.autoSaveAndCloseAfterPrintingReceipt',
          },
          {
            componentType: 'checkbox',
            label: 'Automatically save & close inbound ticket after printing scale receipt',
            path: 'purchaseSettings.inboundTickets.autoSaveAndCloseAfterPrintingScaleReceipt',
          },
        ],
      },
      {
        type: 'list',
        treatment: 'show_decimal_precision_setting',
        title: 'Decimal Precision',
        subTitle: 'This field controls the number of decimal places exposed in the purchasing module',
        listComponents: [
          {
            componentType: 'select',
            label: 'Number of decimal places exposed in user interface',
            items: PRECISION_OPTIONS,
            path: 'purchaseSettings.inboundTickets.decimalPrecision',
          },
        ],
      },
      {
        type: 'list',
        treatment: 'GREEN-4503-show-weight-decimal-setting',
        title: 'Toggle Decimal Display for Weights',
        subTitle: `Easily switch between displaying weights with or without decimals on your inbound ticket materials list.
        When enabled, the decimals will be rounded to the nearest tenth for a greater level of precision.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Show rounded decimals (to nearest tenth) for inbound ticket weights',
            path: 'purchaseSettings.inboundTickets.showWeightDecimals',
          },
        ],
      },
      {
        type: 'list',
        title: 'Autosave Inbound Tickets',
        subTitle:
          'This setting, when checked, automatically saves changes made on the Inbound Tickets screen after 10 seconds of inactivity.',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Autosave changes made on Inbound Tickets',
            path: 'purchaseSettings.inboundTickets.enableAutosave',
          },
        ],
      },
      {
        type: 'list',
        title: 'Weight Differential Threshold',
        subTitle: `GreenSpark will warn you whenever the difference between Net Truck Weight
          and net Material Weight is greater than the threshold you enter here, either in terms of % or {DEFAULT_YARD_UM}.`,
        itemsInRowDirection: true,
        listComponents: [
          {
            componentType: 'input',
            label: '',
            labelInput: 'Threshold Amount',
            path: 'purchaseSettings.inboundTickets.thresholdAmount',
          },
          {
            componentType: 'select',
            label: '',
            items: THRESHOLD_UM_OPTIONS,
            path: 'purchaseSettings.inboundTickets.thresholdUM',
          },
        ],
      },
      {
        type: 'list',
        title: 'Capture Axle Weights',
        subTitle: 'When activated, individual axle weights can be taken for IBTs and OBTs.',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Enable axle weight recording for trucks',
            path: 'purchaseSettings.inboundTickets.enableAxleWeights',
          },
          {
            componentType: 'select',
            label: 'Default number of truck axles on new inbound tickets',
            items: NUMBER_OF_AXLES_OPTIONS,
            path: 'purchaseSettings.inboundTickets.defaultNumberOfAxles',
            disabledBy: [
              {
                key: 'purchaseSettings.inboundTickets.enableAxleWeights',
                value: false,
              },
            ],
          },
        ],
      },
    ],
  },
  payment: {
    category: 'purchaseSettings',
    subCategory: 'payment',
    settingsList: [
      {
        type: 'list',
        title: 'Automatically Increment Check Number',
        subTitle:
          'When checked, GreenSpark will automatically increment check numbers to match the numbers on your checks.',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Automatically increment check numbers in GreenSpark',
            path: 'purchaseSettings.payment.automaticallyIncrementCheckNumber',
          },
          {
            componentType: 'input',
            label: 'Current check number:',
            path: 'purchaseSettings.payment.startingCheckNumber',
            disabledBy: [
              {
                key: 'purchaseSettings.payment.automaticallyIncrementCheckNumber',
                value: false,
              },
            ],
            inputType: 'number',
          },
        ],
      },
      {
        type: 'customCheckTemplates',
        treatment: 'GREEN-6060-custom-check-templates',
        title: 'Customize Check Layout',
        subTitle: 'Customize the way Greenspark prints checks to work with any check template.',
        subTitle2: "Remember to update the current check number when you've finished printing sample checks.",
        path: 'purchaseSettings.payment.checkLayout',
      },
      {
        type: 'upload',
        treatment: 'GREEN-9533-add-check-signature-into-app',
        title: 'Check Signature',
        subTitle: 'Upload a signature for use on printed checks. (Must be an image file smaller than 5MB.)',
        buttonCaption: 'Upload Signature',
        path: 'purchaseSettings.payment.checkSignatureImage',
      },
      {
        type: 'list',
        treatment: 'show_payment_rounding_setting',
        title: 'Payment Rounding',
        subTitle: 'GreenSpark will automatically round scrap purchases to nearest cent based on your selection below.',
        listComponents: [
          {
            componentType: 'select',
            label: 'Round payments:',
            items: PAYMENT_ROUNDING_OPTIONS,
            path: 'purchaseSettings.payment.roundCashPayments',
          },
          {
            componentType: 'select',
            label: 'Rounding direction:',
            items: PAYMENT_ROUNDING_DIRECTION_OPTIONS,
            path: 'purchaseSettings.payment.roundingDirection',
          },
          {
            componentType: 'checkbox',
            label: 'Only round cash and ATM payments',
            path: 'purchaseSettings.payment.roundOnlyATMandCash',
          },
        ],
      },
      {
        type: 'list',
        title: 'Payment Type Threshold',
        subTitle:
          'If a ticket is above the set Ticket Value Threshold, you will receive a reminder to pay with the set allowed payment type(s).',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Set a payment type threshold',
            path: 'purchaseSettings.payment.paymentThreshold.hasThreshold',
          },
          {
            componentType: 'multiInputRow',
            inputList: [
              {
                label: 'Ticket Value Threshold',
                inputType: 'number',
                path: 'purchaseSettings.payment.paymentThreshold.ticketValueThreshold',
              },
              {
                label: 'Allowed Payment Types',
                inputType: 'dropdown',
                useFilteredPaymentMethods: true,
                path: 'purchaseSettings.payment.paymentThreshold.allowedPaymentTypes',
                defaultValue: [],
                selectProps: {
                  multiple: true,
                },
              },
            ],
          },
        ],
      },
      {
        type: 'list',
        title: 'Commodity Type Thresholds',
        subTitle:
          'If a ticket is above the set Ticket Value Threshold for the given commodity type, you will receive a reminder to pay with the set allowed payment type(s).',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Set a FERROUS cost threshold',
            path: 'purchaseSettings.payment.commodityType.ferrousCostThreshold.hasThreshold',
          },
          {
            componentType: 'multiInputRow',
            inputList: [
              {
                label: 'Ferrous Value Threshold',
                inputType: 'number',
                path: 'purchaseSettings.payment.commodityType.ferrousCostThreshold.ticketValueThreshold',
              },
              {
                label: 'Allowed Payment Types',
                inputType: 'dropdown',
                useFilteredPaymentMethods: true,
                path: 'purchaseSettings.payment.commodityType.ferrousCostThreshold.allowedPaymentTypes',
                defaultValue: [],
                selectProps: {
                  multiple: true,
                },
              },
            ],
          },
          {
            componentType: 'checkbox',
            label: 'Set a NON-FERROUS cost threshold',
            path: 'purchaseSettings.payment.commodityType.nonFerrousCostThreshold.hasThreshold',
          },
          {
            componentType: 'multiInputRow',
            inputList: [
              {
                label: 'Non-Ferrous Value Threshold',
                inputType: 'number',
                path: 'purchaseSettings.payment.commodityType.nonFerrousCostThreshold.ticketValueThreshold',
              },
              {
                label: 'Allowed Payment Types',
                inputType: 'dropdown',
                useFilteredPaymentMethods: true,
                path: 'purchaseSettings.payment.commodityType.nonFerrousCostThreshold.allowedPaymentTypes',
                defaultValue: [],
                selectProps: {
                  multiple: true,
                },
              },
            ],
          },
        ],
      },
      {
        type: 'paymentAuthorization',
        title: 'High Balance Payment Authorization',
        subTitle: 'Any ticket above the set Ticket Value Threshold will have to be paid by a yard administrator.',
        path: 'purchaseSettings.payment.highBalancePaymentAuthorization.roleBasedThresholds',
      },
      {
        type: 'list',
        title: 'Default Payment Method',
        subTitle:
          'This will be the default method of payment used for paying your suppliers. ' +
          'Preferred payment methods for individual customers will take priority over this setting.',
        listComponents: [
          {
            componentType: 'select',
            label: '',
            useFilteredPaymentMethods: true,
            path: 'purchaseSettings.payment.yardPreferredPaymentMethod',
          },
        ],
      },
      {
        type: 'list',
        title: 'Customer Fingerprints',
        subTitle: `For compliance reasons, GreenSpark requires a fingerprint to be collected for each ticket payment.
        You can check the box below to override this requirement and automatically store customers' fingerprints for reuse after initial capture.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Allow fingerprint reuse for customers',
            path: 'purchaseSettings.payment.reuseCustomerFingerprint',
          },
        ],
      },
      {
        type: 'list',
        title: 'Compliance Requirements For Payments',
        subTitle:
          'Ensure all necessary information is collected before processing customer payouts. Configure which fields must be completed by the scale operator to maintain compliance and accuracy.',
        listComponents: [
          {
            componentType: 'multiSelect',
            items: COMPLIANCE_FIELDS_ARRAY,
            path: 'purchaseSettings.payment.requiredInYardPaymentFields',
            labelInput: 'Required Fields For Payments',
          },
        ],
      },
    ],
  },
  general: {
    category: 'inventorySettings',
    subCategory: 'general',
    settingsList: [
      {
        type: 'list',
        title: 'Negative Inventory',
        subTitle: `By default, GreenSpark does not allow a yard's inventory (either Work in Process or Finished Goods)
        to have a negative weight value. Selecting the checkbox will override that default setting and allow inventory
        weight to go negative.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Allow negative inventory for materials',
            path: 'inventorySettings.general.allowNegativeInventory',
          },
        ],
      },
    ],
  },
  'outbound-tickets': {
    category: 'inventorySettings',
    subCategory: 'outboundTickets',
    settingsList: [
      // {
      //   type: 'list',
      //   title: 'Customer Weight Reconciliation',
      //   subTitle: `If weights received by a mill or foundry are different than the weights listed on an outbound shipment
      //   ticket from your yard, the below settings determine what wil happen in GreenSpark:`,
      //   listComponents: [
      //     {
      //       componentType: 'checkbox',
      //       label: 'Set yard weights as default customer weights',
      //       path: 'inventorySettings.outboundTickets.setYardWeightsAsDefault',
      //     },
      //     {
      //       componentType: 'radioGroup',
      //       label: 'If customer weight is different than yard weight',
      //       path: 'inventorySettings.outboundTickets.adjustInventoryBy',
      //       items: [
      //         {
      //           id: 'yard-weight',
      //           name: 'Adjust inventory by yard weight',
      //         },
      //         {
      //           id: 'customer-weight',
      //           name: 'Adjust inventory by customer weight',
      //         },
      //       ],
      //       disabledBy: [
      //         {
      //           key: 'inventorySettings.outboundTickets.setYardWeightsAsDefault',
      //           value: true,
      //         },
      //       ],
      //     },
      //   ],
      // },
    ],
  },
  'finished-goods': {
    category: 'inventorySettings',
    subCategory: 'finishedGoods',
    settingsList: [
      {
        type: 'grid',
        title: 'Finished Goods',
        subTitle: 'List of finished goods',
        rowType: 'Finished Good Type',
        generateColumns: FINISHED_GOODS_COLUMNS,
        path: 'inventorySettings.finishedGoods.listOfFinishedGoods',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  compliance: {
    category: 'customerSettings',
    subCategory: 'compliance',
    settingsList: [
      {
        type: 'list',
        title: 'Collect Race and Weight of Contacts',
        subTitle: 'When checked, you will be given the option to fill out the race and weight of customer contacts',
        listComponents: [
          {
            componentType: 'checkbox',
            label: 'Collect weight and race information',
            path: 'customerSettings.compliance.enableRaceAndWeightCollection',
          },
        ],
      },
    ],
  },
  'supplier-groupings': {
    category: 'customerSettings',
    subCategory: 'supplierGroupings',
    settingsList: [
      {
        type: 'grid',
        title: 'Supplier Types',
        subTitle: '',
        rowType: 'Supplier Type',
        generateColumns: SUPPLIER_TYPE_SETTINGS_COLUMNS,
        path: 'customerSettings.supplierGroupings.supplierTypes',
        dataGridProps: {
          density: 'standard',
        },
      },
      {
        type: 'grid',
        title: 'Vendor Classes',
        subTitle: '',
        rowType: 'Vendor Class',
        generateColumns: VENDOR_CLASS_SETTINGS_COLUMNS,
        path: 'customerSettings.supplierGroupings.vendorClasses',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  'cash-drawers': {
    category: 'purchaseSettings',
    subCategory: 'cashDrawers',
    settingsList: [
      {
        type: 'reportWithBlade',
        title: 'Activity Log',
        subTitle: 'List of all cash activity that has taken place',
        rowType: 'Activity',
        bladeTitle: 'Add Cash Drawer Activity',
        generateColumns: CASH_DRAWERS_ACTIVITY_COLUMNS,
        bladeActionOnSuccess: 'addActivity',
        fieldsToWatch: ['activityType'],
        bladeComponents: [
          {
            componentType: 'dynamic',
            conditions: [
              {
                key: 'activityType',
                value: 'transfer',
              },
            ],
            defaultComponent: {
              componentType: 'select',
              label: 'Drawer',
              path: 'accountId',
              md: 6,
              items: [], // this gets set in "SettingsContainer" because the list of items comes from the BE
              disabled: true,
            },
            conditionalComponent: {
              componentType: 'doubleSelect',
              label1: 'Transfer From',
              path1: 'fromAccountId',
              label2: 'Transfer To',
              path2: 'toAccountId',
              md: 6,
              items: [], // this gets set in "SettingsContainer" because the list of items comes from the BE
              disabled: true,
            },
          },
          {
            componentType: 'select',
            label: 'Activity Type',
            path: 'activityType',
            items: ACTIVITY_TYPE_OPTIONS,
            md: 6,
          },
          {
            componentType: 'input',
            label: 'Cash Amount',
            path: 'amount',
            md: 6,
            disabled: false,
            isAmount: true,
            required: true,
          },
          {
            componentType: 'input',
            label: 'Notes',
            path: 'notes',
            md: 6,
            disabled: false,
            isAmount: false,
            required: false,
          },
          {
            componentType: 'dynamic',
            conditions: [
              {
                key: 'activityType',
                value: 'transfer',
              },
            ],
            defaultComponent: {
              componentType: 'date-input',
              label: 'Transaction Date',
              path: 'paymentDate',
              dataTag: 'activity-transaction-datetime-picker',
              md: 6,
              disabled: false,
              isAmount: false,
              required: false,
            },
            conditionalComponent: {
              componentType: 'date-input',
              label: 'Transaction Date',
              path: 'transferDate',
              dataTag: 'transfer-transaction-datetime-picker',
              md: 6,
              disabled: false,
              isAmount: false,
              required: false,
            },
          },
        ],
      },
    ],
  },
  'scale-view': {
    category: 'purchaseSettings',
    subCategory: 'scaleView',
    settingsList: [
      {
        type: 'scaleViewMaterials',
        title: 'Inbound Ticket Material Fields',
        subTitle: `Reorder and change the color of the commodities on your Inbound Tickets
        in scale view. Customize the order and color of materials by clicking on a commodity.`,
        path: 'purchaseSettings.scaleView.customizedMaterialFields',
      },
    ],
  },
  accounts: {
    category: 'purchaseSettings',
    subCategory: 'accounts',
    settingsList: [
      {
        type: 'gridWithBlade',
        title: 'Accounts',
        subTitle: 'View accounts that have been linked to GreenSpark',
        rowType: 'Account',
        bladeTitle: 'Add Account',
        generateColumns: ACCOUNTS_COLUMNS,
        bladeActionOnSuccess: 'addAccount',
        path: 'purchaseSettings.accounts.accounts',
        bladeComponents: [
          {
            componentType: 'input',
            label: 'Account Name',
            path: 'accountName',
            md: 6,
            disabled: false,
            required: true,
          },
          {
            componentType: 'select',
            label: 'Payment Type',
            path: 'type',
            items: ACCOUNT_PAYMENT_TYPES.filter(type => type.name !== 'ATM'),
            md: 6,
          },
          {
            componentType: 'input',
            label: 'Ledger Code',
            path: 'ledgerCode',
            md: 6,
            disabled: false,
            required: false,
          },
        ],
      },
    ],
  },
  'load-board': {
    category: 'dispatchSettings',
    subCategory: 'loadBoard',
    settingsList: [
      {
        type: 'list',
        title: 'Dispatch Ticket Roll Over',
        subTitle: `Checking this box will automatically move your previous day's scheduled, incomplete dispatch tickets to the current day.`,
        listComponents: [
          {
            componentType: 'checkbox',
            label: "Roll over prior days' tickets",
            path: 'dispatchSettings.loadBoard.showPastLoadBoard',
          },
        ],
      },
      {
        type: 'list',
        treatment: '',
        title: 'Default Dispatch Ticket Type',
        subTitle:
          'This is the trip type new dispatch tickets will default to. Set this to your most common trip type to save time.',
        listComponents: [
          {
            componentType: 'select',
            label: 'Trip Type',
            items: DISPATCH_TICKET_TYPES_ARRAY,
            path: 'dispatchSettings.loadBoard.defaultTripType',
          },
        ],
      },
      {
        type: 'list',
        title: 'Scheduling Offset',
        subTitle: `This is the number of days to offset the default scheduled date for dispatch tickets. For example, if you set this to 1, the default scheduled date will be tomorrow.`,
        listComponents: [
          {
            componentType: 'input',
            label: 'Default Scheduling Offset (Days)',
            inputType: 'number',
            path: 'dispatchSettings.loadBoard.defaultSchedulingOffset',
          },
        ],
      },
      {
        type: 'customerSupplierTypeMultiSelect',
        title: 'Filter Customers by Supplier Type',
        treatment: '',
        path: 'dispatchSettings.loadBoard.supplierTypesFilter',
        subTitle:
          'Choose the supplier types you want to use as filters for your dispatch ticket customer selection. ' +
          "This will help you narrow down your customer list on based on their associated supplier types, making it easier to find who you're looking for.",
      },
    ],
  },
  drivers: {
    category: 'dispatchSettings',
    subCategory: 'drivers',
    settingsList: [
      {
        type: 'grid',
        title: 'Drivers',
        subTitle: 'Manage the drivers that can be added to dispatch tickets',
        rowType: 'Driver',
        generateColumns: DRIVER_COLUMNS,
        path: 'dispatchSettings.drivers.drivers',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  'asset-types': {
    category: 'dispatchSettings',
    subCategory: 'assetTypes',
    settingsList: [
      {
        type: 'grid',
        title: 'Truck Types',
        subTitle: '',
        rowType: 'Truck Types',
        generateColumns: ASSET_TYPES_SETTINGS_COLUMNS,
        path: 'dispatchSettings.assetTypes.truckTypes',
        dataGridProps: {
          density: 'standard',
        },
      },
      {
        type: 'grid',
        title: 'Trailer Types',
        subTitle: '',
        rowType: 'Trailer Types',
        generateColumns: ASSET_TYPES_SETTINGS_COLUMNS,
        path: 'dispatchSettings.assetTypes.trailerTypes',
        dataGridProps: {
          density: 'standard',
        },
      },
      {
        type: 'grid',
        title: 'Container Types',
        subTitle: '',
        rowType: 'Container Types',
        generateColumns: ASSET_TYPES_SETTINGS_COLUMNS,
        path: 'dispatchSettings.assetTypes.containerTypes',
        dataGridProps: {
          density: 'standard',
        },
      },
    ],
  },
  ledger: {
    category: 'erpSettings',
    subCategory: 'general',
    settingsList: [
      {
        type: 'list',
        title: 'Accounting Software Bills Sync',
        subTitle:
          'Choose whether you want GreenSpark Bills to use the bill "effective date" or "completion date" when syncing with your accounting software.',
        listComponents: [
          {
            componentType: 'select',
            label: '',
            items: BILL_SYNC_DATE_SOURCE_OPT,
            path: 'erpSettings.general.syncBillsBasedOn',
          },
        ],
      },
    ],
  },
  'contracts-general': {
    category: 'contractSettings',
    subCategory: 'autoCloseContracts',
    settingsList: [
      {
        type: 'list',
        title: 'Auto-close Contracts',
        subTitle: 'Choose whether contracts close based on their expiration date or based on their fulfillment status.',
        listComponents: [
          {
            componentType: 'select',
            label: 'Auto-close contracts',
            path: 'contractSettings.autoCloseContracts.autoCloseSetting',
            items: AUTO_CLOSE_CONTRACT_OPTIONS,
          },
          {
            componentType: 'autoCloseWarning',
            path: 'contractSettings.autoCloseContracts',
          },
        ],
      },
    ],
  },
}
